import { Check, Copy, Inbox } from "lucide-react";
import { useState } from "react";

import { AllowlistManager } from "@/components/inbox/AllowlistManager";
import {
  CardActionButton,
  StatusPill,
} from "@/components/dashboard/IntegrationCardShell";
import { BentoCard } from "@/components/ui/BentoCard";

import type { AllowlistManagerProps } from "@/components/inbox/AllowlistManager";

export interface EmailIntegrationContentProps {
  agentEmailAddress: string | undefined;
  allowlist: AllowlistManagerProps;
  onCreateEmailAddress: () => Promise<void>;
}

/**
 * Inner content — reusable in both BentoCard and FloatingWindow contexts.
 * Mirrors the IntegrationCardShell anatomy (tile → title+pill → description
 * → content → divided footer) so the email card reads as part of the set.
 */
export function EmailIntegrationContent({ agentEmailAddress, allowlist, onCreateEmailAddress }: EmailIntegrationContentProps) {
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(false);

  function handleCopy() {
    if (!agentEmailAddress) return;
    navigator.clipboard.writeText(agentEmailAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <>
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-home-ink/[0.05] text-home-ink dark:bg-white/10 dark:text-white">
        <Inbox className="h-5 w-5" strokeWidth={2} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1">
        <h3 className="font-body text-[15px] font-semibold text-home-ink dark:text-white">
          Email
        </h3>
        {agentEmailAddress && (
          <StatusPill variant="active" pulse>
            Active
          </StatusPill>
        )}
      </div>

      <p className="mt-1.5 font-body text-[13px] leading-relaxed text-home-ink/55 dark:text-white/55">
        Your agent&apos;s own inbox
      </p>

      <div className="mt-4">
        {agentEmailAddress ? (
          <>
            <button
              onClick={handleCopy}
              type="button"
              className="flex w-full items-center gap-2 rounded-full bg-home-ink/[0.05] px-4 py-2.5 text-left transition-colors hover:bg-home-ink/10 dark:bg-white/10 dark:hover:bg-white/15"
            >
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-home-ink dark:text-white">
                {agentEmailAddress}
              </span>
              {copied ? (
                <Check className="h-4 w-4 shrink-0 text-accent-green" />
              ) : (
                <Copy className="h-4 w-4 shrink-0 text-home-ink/40 dark:text-white/40" />
              )}
            </button>
            <p className="mt-2.5 font-body text-[12px] leading-relaxed text-home-ink/45 dark:text-white/45">
              Forward emails from Gmail: Settings &rarr; Forwarding &rarr; Add
              this address.
            </p>
          </>
        ) : (
          <CardActionButton
            tone="primary"
            disabled={creating}
            onClick={async () => {
              setCreating(true);
              await onCreateEmailAddress();
            }}
          >
            {creating ? "Creating…" : "Create email inbox"}
          </CardActionButton>
        )}
      </div>

      {agentEmailAddress && (
        <div className="mt-auto pt-5">
          <div className="border-t border-home-ink/8 pt-4 dark:border-white/10">
            <AllowlistManager
              entries={allowlist.entries}
              onAdd={allowlist.onAdd}
              onRemove={allowlist.onRemove}
            />
          </div>
        </div>
      )}
    </>
  );
}

/** BentoCard wrapper used on the integrations page. */
export function EmailIntegrationPanel(props: EmailIntegrationContentProps) {
  return (
    <BentoCard hover className="rounded-3xl p-6">
      <EmailIntegrationContent {...props} />
    </BentoCard>
  );
}
